// Markdown processing and text utility functions

// Function to convert single asterisks to double asterisks for proper bold formatting in ReactMarkdown
export const formatBoldText = (text) => {
  // Make sure we don't double up existing double asterisks
  let processedText = text;
  // First replace all double asterisks temporarily to avoid processing them
  processedText = processedText.replace(/\*\*/g, '§BOLD§');
  // Now convert single asterisks to double
  processedText = processedText.replace(/\*([^§\n]+?)\*/g, '**$1**');
  // Restore original double asterisks
  processedText = processedText.replace(/§BOLD§/g, '**');
  return processedText;
};

// Ensure proper newlines for ReactMarkdown
export const ensureProperNewlines = (text) => {
  // For ReactMarkdown, we need to respect newlines exactly as they are in the source
  // Two spaces at end of line forces a single newline in markdown

  // Initial text
  let formattedText = text;

  // First, make sure there are no indents at the beginning of numbered list items
  // This uses a global regex to catch all occurrences
  formattedText = formattedText.replace(/^[ \t]+(\d+\.\s+)/gm, '$1');

  // Also remove indents for consecutive list items
  formattedText = formattedText.replace(/\n[ \t]+(\d+\.\s+)/g, '\n$1');

  // Make sure there are no empty lines between consecutive numbered list items
  formattedText = formattedText.replace(
    /(\d+\.\s+.*?)(\n+)(?=\d+\.\s+)/g,
    '$1\n',
  );

  // Add two spaces to the end of each line (except blank lines and those already with spaces)
  // This forces ReactMarkdown to render a single newline
  formattedText = formattedText.replace(/([^\s])\n([^\n])/g, '$1  \n$2');

  return formattedText;
};

// Clean the text of problematic characters
export const cleanText = (text) => {
  return (
    text
      // Replace invisible Unicode separator characters with regular space
      .replace(/[\u2060\u200B\u200C\u200D\u2064]/g, ' ')
      // Replace problematic Unicode characters
      .replace(/[\u2000-\u206F]/g, ' ')
      // Replace other potentially problematic characters
      .replace(/[–—]/g, '-')
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      // Replace the specific character seen in the screenshot (if identifiable)
      .replace(/⁠/g, '')
  );
};

// Preprocess markdown for consistent formatting
export const preprocessMarkdown = (text) => {
  // First, let's normalize newlines to make processing more consistent
  let processedText = text.replace(/\r\n/g, '\n');

  // Handle tab-initiated paragraphs - preserve tab information for indentation
  // Lines starting with \t should have first-line indentation
  processedText = processedText
    .split('\n')
    .map((line) => {
      // If line starts with tab, mark it for first-line indentation and remove the tab
      if (line.startsWith('\t')) {
        // Remove the tab completely and add marker at the beginning
        const contentAfterTab = line.substring(1).trimStart(); // Also trim any additional spaces
        return `[[TAB_INDENT]]${contentAfterTab}`;
      }
      return line;
    })
    .join('\n');

  // Remove leading whitespace from each line (except for tab-marked lines and intentional indentation)
  // This prevents unwanted horizontal spacing in paragraphs
  processedText = processedText
    .split('\n')
    .map((line) => {
      // Don't trim lines that are part of code blocks, intentional indentation, or tab-marked for indentation
      // But do trim regular text lines to prevent spacing issues
      if (
        line.match(/^[ \t]*[^#\d\-\*•]/) &&
        !line.match(/^[ \t]*```/) &&
        !line.includes('[[TAB_INDENT]]')
      ) {
        return line.trimStart();
      }
      return line;
    })
    .join('\n');

  // Special pre-processing for numbered lists to ensure they're consecutive
  // This will replace any empty lines between numbered items
  processedText = processedText.replace(
    /(\d+\.\s+.*?)(\n+)(?=\d+\.\s+)/g,
    '$1\n',
  );

  // Remove any tabs or extra spaces at the beginning of numbered list items
  processedText = processedText.replace(/(\n)[ \t]*(?=\d+\.\s+)/g, '$1');

  // Now continue with standard processing
  // Mark double newlines with a special marker (but not within numbered lists)
  processedText = processedText.replace(/\n\n/g, '\n[[DOUBLE_NEWLINE]]\n');

  // Replace lines ending with two spaces followed by newline with a forced single linebreak marker
  processedText = processedText.replace(/ {2}\n/g, '[[SINGLE_LINEBREAK]]\n');

  return processedText;
};

// Function to check for placeholders in the content
export const hasPlaceholders = (petitionContent) => {
  // Check for square bracket patterns that indicate missing values
  const placeholderPattern = /\[[^\]]+\]/g;
  const matches = petitionContent.match(placeholderPattern);

  // Filter out common markdown syntax that should not be considered placeholders
  const ignoredPatterns = [
    /\[titleMergeRequest\]/g,
    /\[resultBeforeOtherwise\]/g,
  ];

  if (!matches) return { hasPlaceholders: false, placeholders: [] };

  // Filter out ignored patterns and remove brackets
  const actualPlaceholders = matches
    .filter((match) => {
      return !ignoredPatterns.some((pattern) => pattern.test(match));
    })
    .map((match) => match.replace(/[\[\]]/g, '')) // Remove square brackets
    .filter((value, index, array) => array.indexOf(value) === index); // Remove duplicates

  return {
    hasPlaceholders: actualPlaceholders.length > 0,
    placeholders: actualPlaceholders,
  };
};

// Normalize text content for processing
export const normalizeMarkdownText = (markdownText) => {
  return markdownText
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\n\s*\n\s*\n+/g, '\n\n') // Replace 3+ newlines (with possible whitespace) with 2
    .replace(/\n{3,}/g, '\n\n'); // Backup regex for pure newlines
};
